import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatTile from './StatTile';
import BadgeSheet, { BadgeTile } from './BadgeSheet';
import { apiRequest } from '../../utils';
import '../../pages/user/PublicProfile.css';

/* "Who is here" — its own screen (/who-is-here), reached from a home pill on events that switched
   it on (Machina Content > Website): how many players and captains, which badges they bring
   (rarest first — the experience in the field), and which fun team roles they picked.

   NUMBERS only, never names: the names list beside it is hidden on PLG events, and "12 Kaisers in
   the field" tells nobody who they are. A tap on a badge says what it stands for.
   Renders nothing until there is something to say. */

const roleName = (t, role) =>
  role.translation_key
    ? t(`${role.translation_key}.name`, { ns: 'roles', defaultValue: role.name })
    : role.name;

const WhoIsHere = () => {
  const { t } = useTranslation(['common', 'roles']);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let active = true;
    apiRequest('GET', '/home_who_is_here')
      .then((res) => { if (active) setData(res); })
      .catch(() => { /* non-blocking: the home renders without it */ });
    return () => { active = false; };
  }, []);

  if (!data || !data.players) return null;
  const badges = data.badges || [];
  const roles = data.roles || [];
  const topTeams = data.top_teams || [];
  const topCompanies = data.top_companies || [];

  /* A ranked list of teams or companies by the badge value their players bring. Names of teams
     and companies only — never a person's; this screen stays name-free for people. */
  const ranked = (title, rows, linkFor) => rows.length > 0 && (
    <>
      <header className="pp-section-header mt-4">
        <h2 className="pp-section-title">{title}</h2>
      </header>
      <div className="d-flex flex-column gap-2">
        {rows.map((r, i) => {
          const inner = (
            <>
              <span className="fw-bold" style={{ width: '1.6rem' }}>{i + 1}</span>
              {r.photo_url
                ? <img src={r.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover' }} />
                : <span style={{ width: 36 }} />}
              <span className="flex-grow-1 text-truncate">{r.name}</span>
              <span className="text-muted small">{t('badgeValueN', { count: r.badges, value: r.value.toLocaleString() })}</span>
            </>
          );
          const to = linkFor(r);
          return to ? (
            <Link key={r.id} to={to} className="d-flex align-items-center gap-2 text-reset text-decoration-none">{inner}</Link>
          ) : (
            <div key={r.id} className="d-flex align-items-center gap-2">{inner}</div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="pp-page">
      <div className="stat-tiles">
        <StatTile tone="teal" icon="users" num={data.players} label={t('whoPlayers')} />
        <StatTile tone="orange" icon="crown" num={data.captains} label={t('whoCaptains')} />
      </div>

      {badges.length > 0 && (
        <>
          <header className="pp-section-header mt-4">
            <h2 className="pp-section-title">{t('badgesInTheField')}</h2>
          </header>
          <div className="pp-awards-grid">
            {badges.map((b) => (
              <BadgeTile key={b.id} badge={b} onOpen={setOpen}>
                <div className="pp-award-event">×{b.count}</div>
              </BadgeTile>
            ))}
          </div>
        </>
      )}

      {ranked(t('topTeamsByBadges'), topTeams, (r) => `/public-team/${r.id}`)}
      {ranked(t('topCompaniesByBadges'), topCompanies, (r) => `/public-company/${r.id}`)}

      {roles.length > 0 && (
        <>
          <header className="pp-section-header mt-4">
            <h2 className="pp-section-title">{t('teamRolesInTheField')}</h2>
          </header>
          <div className="d-flex flex-wrap gap-2">
            {roles.map((r) => {
              const color = r.color || '#64748B';
              return (
                <span key={r.id} className="badge rounded-pill"
                      style={{ background: `${color}1F`, color, border: `1px solid ${color}`,
                               fontSize: '0.8rem', fontWeight: 600 }}>
                  {r.emoji || '⭐'} {roleName(t, r)} · {r.count}
                </span>
              );
            })}
          </div>
        </>
      )}

      <BadgeSheet badge={open} onClose={() => setOpen(null)} />
    </div>
  );
};

export default WhoIsHere;
